import Filter from 'src/features/pharmacies/shared/ui/Filter/Filter';
import PharmacyScrollFilters from 'src/features/pharmacies/shared/ui/PharmacyScrollFilters/PharmacyScrollFilters';

import MaterialIcon from '@/components/atom/MaterialIcon';
import ClickLogger from '@/components/common/ClickLogger';
import BottomActionSheet from '@/components/molecules/BottomActionSheet';
import { PharmacyDdAction } from '@/constants/ddActions/pharmacy';
import { NearbyPharmacyFilterCriteria } from '@/features/pharmacies/shared/hooks/usePharmacyFilterViewModel';
import AddressesModal from '@/features/pharmacies/shared/ui/Modals/AddressesModal';
import { Tab } from '@/hooks/pharmacy/useFindPharmacyCategoryTabs';
import useModal from '@/hooks/useModal';
import theme from '@/styles/theme';
import { Client } from '@/types/app';
import { MODAL_TYPE } from '@/types/modal';
import { ENearbyPharmacySearchSortType } from '@/types/pharmacy';
import { cn } from '@/utils/style';
import MedicineInfoBottomSheet from '../../pharmacies-find/ui/MedicineInfoBottomSheet';
import { MedicineInfo, useNonBenefitPharmacyFilterViewModel } from '../viewModels/useNonBenefitPharmacyFilterViewModel';

interface Props {
  client: Client;
  selectedTab: Tab | null;
  defaultSort: ENearbyPharmacySearchSortType;
  currentDongLi: string;
  diagnosisId?: string;
  onApplyFilter: (filter: NearbyPharmacyFilterCriteria) => void;
  onClickCurrentLocation?: () => void;
  medicines?: MedicineInfo[];
}

const NonBenefitPharmacyHeaderFilter = (props: Props) => {
  const { showModal, closeModal } = useModal();

  const viewModel = useNonBenefitPharmacyFilterViewModel({
    client: props.client,
    selectedTab: props.selectedTab,
    defaultSort: props.defaultSort,
    onApplyFilter: props.onApplyFilter,
    onClickCurrentLocation: props.onClickCurrentLocation,
  });

  const openAddressSelectModal = () => {
    const addressOptions = viewModel.addressActions.openAddressSelectModal();

    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <AddressesModal
            client={props.client}
            addresses={addressOptions.addresses}
            onOpenAddressListScreen={addressOptions.onOpenAddressListScreen}
            onRequestCurrentPosition={() => {
              addressOptions.onRequestCurrentPosition?.();
              closeModal();
            }}
          />
        ),
        onClickOverlay: closeModal,
      },
    });
  };

  return (
    <>
      <Filter>
        <PharmacyScrollFilters
          pharmacyFilters={viewModel.searchFiltersHook.pharmacyFilterListWithoutIncludeDiagnosisPriceFilter}
          onClickSearchCommonFilter={viewModel.handleFilterClick}
          onClickListFilter={viewModel.handleFilterClick}
          selectedFilters={viewModel.localFilterCriteria.selectedCommonFilters}
        >
          <ClickLogger
            params={{
              eventName: PharmacyDdAction.ClickLocation,
              payload: {
                location: props.currentDongLi,
              },
            }}
          >
            <Filter.LargeFilter onClick={openAddressSelectModal}>
              <MaterialIcon name='my_location' size={16} color={theme.color.gray700} filled />
              <span>{props.currentDongLi}</span>
            </Filter.LargeFilter>
          </ClickLogger>

          <div className='mx-[4px] h-full min-w-[1px] bg-gray-30'></div>

          <ClickLogger
            params={{
              eventName: PharmacyDdAction.ClickSort,
            }}
          >
            <Filter.LargeFilter onClick={viewModel.openSortBottomSheet}>
              <MaterialIcon name='swap_vert' size={16} color={theme.color.gray700} filled />
              <span>{viewModel.localFilterCriteria.selectedSortMethod.title}</span>
            </Filter.LargeFilter>
          </ClickLogger>
        </PharmacyScrollFilters>
      </Filter>

      {/* 처방약/처방단위/진료비 정보 섹션 */}
      {viewModel.shouldShowTreatmentMedicineFilter() && viewModel.localFilterCriteria.selectedMedicine && (
        <div className='flex w-full flex-col divide-y divide-solid divide-gray-30 bg-white'>
          <div className='flex h-10 w-full flex-row items-center gap-[12px]'>
            <div className='flex w-20 items-center gap-[2px] pl-6'>
              <div className='typography-ui-13sb text-gray-800'>처방약</div>
              <button
                className='flex cursor-pointer items-center justify-center border-0 bg-none'
                onClick={viewModel.handleMedicineInfoClick}
              >
                <img src='/images/pharmacy/info_gray.svg' alt='정보' width='14' height='14' />
              </button>
            </div>
            <div className='scrollbar-hide flex flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap pr-12'>
              {viewModel.treatmentMedicines?.map((medicine) => (
                <button
                  key={medicine.treatmentMedicineId}
                  className={cn(
                    'typography-ui-13sb flex h-10 items-center px-1',
                    viewModel.isMedicineSelected(medicine) ? 'text-primary-700' : 'typography-ui-13m text-gray-600'
                  )}
                  onClick={() => viewModel.handleMedicineClick(medicine)}
                >
                  {medicine.name}
                </button>
              ))}
            </div>
          </div>
          <div className='flex h-10 w-full flex-row items-center gap-[12px]'>
            <div className='flex w-20 items-center pl-6'>
              <div className='typography-ui-13sb text-gray-800'>처방단위</div>
            </div>
            <div className='scrollbar-hide flex flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap pr-12'>
              {viewModel.getSelectedMedicineUnits().map((unit) => (
                <button
                  key={unit.dispensationUnit}
                  className={cn(
                    'typography-ui-13sb flex h-10 items-center px-1',
                    viewModel.isDispensationUnitSelected(unit) ? 'text-primary-700' : 'typography-ui-13m text-gray-600'
                  )}
                  onClick={() => viewModel.handleDispensationUnitClick(unit)}
                >
                  {unit.dispensationUnitDisplayName}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 정렬 바텀시트 */}
      <BottomActionSheet isOpened={viewModel.isSortBottomSheetOpen} onClickOverlay={viewModel.closeSortBottomSheet}>
        <div className='relative'>
          <div className='typography-ui-20sb px-[12px] pb-4 text-left text-gray-900'>어떤 순서로 정렬할까요?</div>

          <div className='flex flex-col'>
            {viewModel.searchFiltersHook.pharmacySorts.map((sort, index) => (
              <div
                key={`sort-${index}`}
                className='flex cursor-pointer items-center justify-between rounded-lg px-3 py-4'
                onClick={() => viewModel.handleSortSelected(sort)}
              >
                <div className='typography-ui-17m text-gray-900'>{sort.title}</div>
                {sort.type === viewModel.localFilterCriteria.selectedSortMethod.type && (
                  <div className='flex items-center justify-center'>
                    <MaterialIcon name='check' color={theme.color.primary600} size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </BottomActionSheet>

      {/* 약 정보 바텀시트 */}
      {viewModel.isMedicineInfoOpen && (
        <MedicineInfoBottomSheet
          diagnosisTypeId={props.selectedTab?.diagnosisTypeId || null}
          onClickClose={viewModel.handleCloseMedicineInfo}
        />
      )}
    </>
  );
};

export default NonBenefitPharmacyHeaderFilter;
